import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { HouseholdSwitcher } from './HouseholdSwitcher'
import { useAuth } from '../hooks/useAuth'
import { useProfile } from '../hooks/useProfile'
import { memberColors } from '../styles/tokens'
import { getInitials, cn } from '../lib/utils'
import { WhatsNewButton } from './WhatsNewPanel'

const GearIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
)

const SignOutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
)

export const Header = () => {
  const { signOut } = useAuth()
  const { data: profile } = useProfile()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const color = memberColors[profile?.member_color ?? 'violet']
  const initials = getInitials(profile?.display_name ?? '')

  return (
    <header className="fixed top-0 inset-x-0 z-30 h-14 bg-surface border-b border-border flex items-center justify-between px-4">
      {/* Left: Household switcher */}
      <HouseholdSwitcher />

      {/* Right: What's new + Avatar + menu */}
      <div className="flex items-center gap-1">
        <WhatsNewButton />
      <div ref={menuRef} className="relative">
        <button
          onClick={() => setMenuOpen((o) => !o)}
          className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          style={{ backgroundColor: color }}
          aria-label="Account menu"
        >
          {profile?.avatar_url ? (
            <img src={profile.avatar_url} alt={profile.display_name} className="w-full h-full rounded-full object-cover" />
          ) : (
            initials || '?'
          )}
        </button>

        {menuOpen && (
          <div className="absolute right-0 top-full mt-1 w-48 bg-surface rounded-base shadow-card border border-border z-50 overflow-hidden">
            {profile?.display_name && (
              <div className="px-4 py-3 border-b border-border">
                <p className="text-sm font-medium text-text-primary truncate">{profile.display_name}</p>
              </div>
            )}
            <div className="py-1">
              <Link
                to="/settings"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-text-primary hover:bg-background transition-colors"
              >
                <GearIcon /> Settings
              </Link>
              <button
                onClick={() => { setMenuOpen(false); signOut() }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors"
              >
                <SignOutIcon /> Sign out
              </button>
            </div>
          </div>
        )}
      </div>
      </div>
    </header>
  )
}
